import React from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link, useRouter } from '@tanstack/react-router';

const backButton = (right = false, size = 16) => <ArrowDown style={{ transform: `rotate(${right ? 270 : 90}deg)`, ...iconSize(size) }} />;
const separator = <ArrowDown style={{ transform: 'rotate(270deg)', ...iconSize(16) }} />;

export enum EVariant {
  default = 'default',
  backButton = 'backButton',
  drillDown = 'drillDown',
}

export type TBackButtonBreadcrumb = {
  id: string | number;
  title: string | number | null | undefined;
  value: string;
  projectionName: string;
  onClick?: () => void;
  path: string;
  shouldNavigateBack?: boolean;
};

export type TDrillDownBreadcrumb = {
  title: string | number | null | undefined;
  value: string;
  onClick?: () => void;
};

export interface MenuItem<TSubMenuItem = any> {
  name: string;
  subMenu: TSubMenuItem[];
}
type BreadCrumbsBaseProps = {
  breadcrumbs?: string[] | TBackButtonBreadcrumb[] | TDrillDownBreadcrumb[];
  projectionName?: string;
};
type DefaultBreadCrumbsProps<TSubMenuItem = any> = BreadCrumbsBaseProps & {
  variant?: EVariant.default;
  menuItems: TDefaultBreadcrumbs<TSubMenuItem>['menuItems'];
};
type NonDefaultBreadCrumbsProps = BreadCrumbsBaseProps & {
  variant: Exclude<EVariant, EVariant.default>;
  /* prevents passing menuItems unless variant is "default" */
  menuItems?: never;
};
export type AvBreadcrumbsProps<TSubMenuItem = any> = DefaultBreadCrumbsProps<TSubMenuItem> | NonDefaultBreadCrumbsProps;

type TDefaultBreadcrumbs<TSubMenuItem = any> = {
  breadcrumbs: string[];
  menuItems: Record<string, MenuItem<TSubMenuItem>[]>;
};

enum BreadcrumbNameMap {
  targets = 'Integrations',
  global = 'Scripts Library',
}

const convertToStartCase = path => path.replace(/(^|_|-)(\w)/g, ($0, $1, $2) => (Number.isNaN(+$2) ? ($1 && ' ') + $2.toUpperCase() : $0));
const getLabel = path => BreadcrumbNameMap[path] || convertToStartCase(path);

const BackButtonBreadcrumb: React.FC<{ breadcrumbs: TBackButtonBreadcrumb[] }> = ({ breadcrumbs = [] }) => {
  const router = useRouter();
  const breadcrumb = breadcrumbs.at(-2);
  const { title, value, onClick: originalOnClick, shouldNavigateBack } = breadcrumb!;
  const onClick = shouldNavigateBack ? () => router.history.back() : originalOnClick;

  return (
    <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
      <Box
        sx={{
          ...flex.itemsCenter,
          gap: 1,
          textDecoration: 'none',
          width: '100%',
          flexGrow: 1,
        }}>
        <Box
          sx={{
            ...flex.itemsCenter,
            gap: 0.5,
            color: 'var(--color-content-interactive-primary-default)',
            fontSize: '13px',
            width: '100%',
          }}>
          <Box onClick={onClick} sx={{ ...flex.itemsCenter, gap: 0.5, cursor: onClick ? 'pointer' : 'default' }}>
            {backButton()} Back To
          </Box>
          <Box
            onClick={onClick}
            className="back-breadcrumb-title"
            sx={{ cursor: onClick ? 'pointer' : 'default', fontWeight: 600, color: 'var(--color-content-base-primary)' }}>
            <AvTooltip>
              {title}: {value}
            </AvTooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const DefaultBreadcrumbs: React.FC<TDefaultBreadcrumbs> = ({ breadcrumbs = [], menuItems = {} }) => (
  <Breadcrumbs separator={separator}>
    {breadcrumbs.map((path, index) => {
      const lastPathName = path?.split('/')?.at(-1);
      const flatMenuItems = Object.keys(menuItems).flatMap(app => menuItems[app].flatMap(({ subMenu }) => subMenu));
      const label = flatMenuItems.find(v => v.path === lastPathName)?.name;
      const last = index === breadcrumbs.length - 1;
      const to = path || `/${breadcrumbs.slice(0, index + 1).join('/')}`;
      return last ? (
        <Box
          key={to}
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--color-content-base-primary)',
          }}>
          {label || getLabel(path)}
        </Box>
      ) : (
        <Box
          component={Link}
          to={to}
          key={to}
          sx={{
            fontSize: '14px',
            color: 'var(--color-content-interactive-primary-default)',
            '&:hover': {
              color: 'var(--color-content-interactive-primary-hover)',
            },
          }}>
          {label || getLabel(path)}
        </Box>
      );
    })}
  </Breadcrumbs>
);

const DrillDownBreadcrumb: React.FC<{ breadcrumbs: TDrillDownBreadcrumb[] }> = ({ breadcrumbs = [] }) =>
  breadcrumbs.map(({ title, value, onClick }, index) => {
    const isLast = index === breadcrumbs.length - 1;
    return (
      <Box
        key={title + value}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          ...flex.row,
          fontWeight: isLast ? 600 : 400,
          gap: 0.5,
          height: '100%',
          color: isLast ? 'var(--color-content-base-primary)' : 'var(--color-content-interactive-primary-default)',
          fontSize: '14px',
        }}
        onClick={onClick}>
        <AvTooltip disabled={index === 0} title={`${title}: ${value}`}>
          <Box
            sx={{
              ...flex.row,
              ...(!isLast
                ? { ':hover': { color: 'var(--color-content-interactive-primary-hover)', textDecorationLine: 'underline' } }
                : undefined),
            }}>
            {value}
          </Box>
        </AvTooltip>
        {!isLast && <Box sx={{ ...flex.row, ...flex.center, pr: 0.5 }}>{backButton(true, 14)}</Box>}
      </Box>
    );
  });

const renderBreadcrumbs = ({ breadcrumbs, variant, menuItems }) =>
  ({
    [EVariant.backButton]: <BackButtonBreadcrumb breadcrumbs={breadcrumbs as TBackButtonBreadcrumb[]} />,
    [EVariant.default]: <DefaultBreadcrumbs breadcrumbs={breadcrumbs as string[]} menuItems={menuItems} />,
    [EVariant.drillDown]: <DrillDownBreadcrumb breadcrumbs={breadcrumbs as TDrillDownBreadcrumb[]} />,
  })[variant];

export const AvBreadcrumbs: React.FC<AvBreadcrumbsProps> = ({ breadcrumbs, variant = EVariant.default, menuItems = {} }) =>
  renderBreadcrumbs({ breadcrumbs, variant, menuItems });
